import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Building2,
  CalendarCheck,
  FileText,
  LogOut,
  MessageSquare,
  ScrollText,
  Settings,
  TriangleAlert,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Capability } from '@/lib/auth/capabilities';
import { Wordmark } from '@/components/ui/Wordmark';
import { ChatScreen } from '@/features/chat/ChatScreen';
import { displayName, initialsOf, roleLabel, useSession } from '@/lib/auth/useSession';
import { signOut } from '@/services/authService';

const NAV: { to: string; label: string; icon: typeof MessageSquare; cap: Capability }[] = [
  { to: '/chat', label: 'Chat', icon: MessageSquare, cap: 'chat.use' },
  { to: '/documents', label: 'Documents', icon: FileText, cap: 'documents.read' },
  { to: '/periods', label: 'Periods', icon: CalendarCheck, cap: 'documents.read' },
  { to: '/conflicts', label: 'Conflicts', icon: TriangleAlert, cap: 'conflicts.read' },
  { to: '/instructions', label: 'Instructions', icon: ScrollText, cap: 'instructions.read' },
];

/**
 * Top bar from Austin's concept: SIMON wordmark, property, user. The property
 * selector is fixed to Town Center for the MVP (one property loaded).
 *
 * Chat lives here, not in its route, so it stays mounted while another tab is
 * open: the open conversation, and an answer still streaming, survive a visit
 * to Documents / Conflicts / Instructions. The /chat route renders nothing.
 */
export function AppShell() {
  const { me, can } = useSession();
  const onChat = useRouterState({ select: (s) => s.location.pathname.startsWith('/chat') });
  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center gap-4 border-b border-line bg-surface px-4">
        <img src="/simons-logo.png" alt="Simons Real Estate Group" className="h-7 w-auto" />
        <span className="h-6 w-px bg-line" aria-hidden />
        <Wordmark />
        <div className="ml-4 flex items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft">
          <Building2 size={15} className="text-sage" />
          <span className="font-semibold text-ink">Town Center</span>
        </div>
        <nav className="ml-6 flex items-center gap-1" aria-label="Main">
          {NAV.filter((n) => can(n.cap)).map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-ink-soft hover:bg-surface-sunken hover:text-ink"
              activeProps={{ className: 'bg-surface-sunken !text-ink' }}
            >
              <Icon size={15} /> {label}
            </Link>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-right leading-tight">
            <div className="text-sm font-semibold text-ink">{displayName(me)}</div>
            <div className="text-xs text-ink-faint">{roleLabel(me?.profile.roles)}</div>
          </div>
          <AvatarMenu initials={initialsOf(me)} />
        </div>
      </header>
      <main className="min-h-0 flex-1">
        {can('chat.use') && (
          <div className="h-full" hidden={!onChat}>
            <ChatScreen />
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}

/** The avatar opens Settings and Sign out. */
function AvatarMenu({ initials }: { initials: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const item = 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-sunken';
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex h-8 w-8 items-center justify-center rounded-full bg-sage text-xs font-bold text-white hover:bg-sage-dark"
      >
        {initials}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-10 z-30 w-44 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-lg"
        >
          <button
            role="menuitem"
            type="button"
            className={item}
            onClick={() => {
              setOpen(false);
              void navigate({ to: '/settings' });
            }}
          >
            <Settings size={15} /> Settings
          </button>
          <button role="menuitem" type="button" className={item} onClick={() => void signOut()}>
            <LogOut size={15} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
